import type { BrainKind } from "./types";

export const EMAIL_KINDS = [
  "stage_signature_pending",
  "stage_deposit_pending",
  "stage_handover_pending",
  "application_review",
  "application_incomplete",
  "onboarding_review",
  "owner_bank_missing",
] as const satisfies readonly BrainKind[];
export type EmailKind = (typeof EMAIL_KINDS)[number];

/** `missing`: the application documents still missing (REQUIRED_DOCS ids). */
type Ctx = { name: string; missing?: readonly string[] };

const DOC_NAMES: Record<"de" | "en", Record<string, string>> = {
  de: {
    id_passport: "Ausweis/Reisepass",
    proof_of_income: "Einkommensnachweis",
  },
  en: { id_passport: "ID/passport", proof_of_income: "proof of income" },
};
/** "Ausweis/Reisepass und Einkommensnachweis" — both when none is known. */
const docList = (lang: "de" | "en", missing: readonly string[] = []) => {
  const names = (missing.length ? missing : Object.keys(DOC_NAMES[lang])).map(
    (d) => DOC_NAMES[lang][d] ?? d
  );
  const and = lang === "de" ? " und " : " and ";
  return names.length > 1
    ? `${names.slice(0, -1).join(", ")}${and}${names[names.length - 1]}`
    : names[0];
};
type Tpl = (c: Ctx) => { subject: string; text: string };

/** "Hallo Anna," — or just "Hallo," when there is no name on file. */
const hi = (word: string, name: string) =>
  name ? `${word} ${name},` : `${word},`;
/** Sie-register kinds greet by the full name ("Guten Tag Müller GmbH,"). */
const OWNER_KINDS: readonly EmailKind[] = [
  "onboarding_review",
  "owner_bank_missing",
];

const T: Record<EmailKind, { de: Tpl; en: Tpl }> = {
  stage_signature_pending: {
    de: (c) => ({
      subject: "Dein Mietvertrag wartet auf deine Unterschrift",
      text: `${hi("Hallo", c.name)}\n\ndein Mietvertrag ist bereit. Bitte unterschreibe ihn, damit wir dein Zimmer für dich reservieren können.\n\nDein coliviq-Team`,
    }),
    en: (c) => ({
      subject: "Your rental contract is waiting for your signature",
      text: `${hi("Hi", c.name)}\n\nyour rental contract is ready. Please sign it so we can hold your room for you.\n\nYour coliviq team`,
    }),
  },
  stage_deposit_pending: {
    de: (c) => ({
      subject: "Erinnerung: Kaution",
      text: `${hi("Hallo", c.name)}\n\nwir haben deine Kaution noch nicht vollständig erhalten. Ohne Kaution können wir die Schlüssel leider nicht übergeben. Bitte überweise den offenen Betrag.\n\nDein coliviq-Team`,
    }),
    en: (c) => ({
      subject: "Reminder: security deposit",
      text: `${hi("Hi", c.name)}\n\nwe haven't received your full security deposit yet. We can't hand over the keys until it arrives, so please transfer the open amount.\n\nYour coliviq team`,
    }),
  },
  stage_handover_pending: {
    de: (c) => ({
      subject: "Bitte fülle dein Einzugsprotokoll aus",
      text: `${hi("Hallo", c.name)}\n\nbitte fülle dein Einzugsprotokoll in deinem coliviq-Konto aus, damit wir deinen Einzug abschließen können.\n\nDein coliviq-Team`,
    }),
    en: (c) => ({
      subject: "Please complete your move-in protocol",
      text: `${hi("Hi", c.name)}\n\nplease complete your move-in protocol in your coliviq account so we can finish your move-in.\n\nYour coliviq team`,
    }),
  },
  application_review: {
    de: (c) => ({
      subject: "Deine Bewerbung bei coliviq",
      text: `${hi("Hallo", c.name)}\n\nvielen Dank für deine Bewerbung. \n\nDein coliviq-Team`,
    }),
    en: (c) => ({
      subject: "Your coliviq application",
      text: `${hi("Hi", c.name)}\n\nthank you for your application. \n\nYour coliviq team`,
    }),
  },
  application_incomplete: {
    de: (c) => ({
      subject: "Deiner Bewerbung fehlen noch Unterlagen",
      text: `${hi("Hallo", c.name)}\n\nfür deine Bewerbung fehlt uns noch: ${docList("de", c.missing)}. Bitte lade sie in deinem coliviq-Konto hoch.\n\nDein coliviq-Team`,
    }),
    en: (c) => ({
      subject: "Your application is missing documents",
      text: `${hi("Hi", c.name)}\n\nwe're still missing the following for your application: ${docList("en", c.missing)}. Please upload them in your coliviq account.\n\nYour coliviq team`,
    }),
  },
  onboarding_review: {
    de: (c) => ({
      subject: "Ihr Onboarding bei coliviq",
      text: `${hi("Guten Tag", c.name)}\n\nvielen Dank für Ihre Angaben. \n\nIhr coliviq-Team`,
    }),
    en: (c) => ({
      subject: "Your coliviq onboarding",
      text: `${hi("Hello", c.name)}\n\nthank you for your details. \n\nYour coliviq team`,
    }),
  },
  owner_bank_missing: {
    de: (c) => ({
      subject: "Bankverbindung für Ihre Auszahlungen",
      text: `${hi("Guten Tag", c.name)}\n\nfür Ihre Mietauszahlungen benötigen wir noch Ihre Bankverbindung (Kontoinhaber, IBAN). Bitte senden Sie uns diese per Antwort auf diese E-Mail.\n\nIhr coliviq-Team`,
    }),
    en: (c) => ({
      subject: "Bank details for your payouts",
      text: `${hi("Hello", c.name)}\n\nto pay out your rent we still need your bank details (account holder, IBAN). Please reply to this email with them.\n\nYour coliviq team`,
    }),
  },
};

/** `ctx.name` is the person's full name; tenants are greeted by the first. */
export function emailTemplate(kind: BrainKind, lang: "de" | "en", ctx: Ctx) {
  const t = (T as Partial<Record<BrainKind, { de: Tpl; en: Tpl }>>)[kind];
  if (!t) return null;
  const full = ctx.name.trim();
  const name = OWNER_KINDS.includes(kind as EmailKind)
    ? full
    : full.split(/\s+/)[0];
  return t[lang]({ name, missing: ctx.missing });
}
